import { supabase } from '../lib/supabase';

export type CampaignType = 'call' | 'email' | 'whatsapp' | 'linkedin' | 'omni' | 'inbound';

const CAMPAIGN_TYPE_TO_AGENT: Record<string, string> = {
  call: 'callagent',
  whatsapp: 'whatsappagent',
  email: 'emailagent',
  linkedin: 'linkedinagent',
};

export const getAgentForCampaignType = (campaignType: CampaignType): string | null => {
  return CAMPAIGN_TYPE_TO_AGENT[campaignType] || null;
};

export const mapActionToCampaignType = (action: string): CampaignType | null => {
  const actionMap: Record<string, CampaignType> = {
    'Call': 'call',
    'WhatsApp': 'whatsapp',
    'Email': 'email',
    'LinkedIn': 'linkedin',
    'Omni-Channel': 'omni',
    'Inbound': 'inbound'
  };

  return actionMap[action] || null;
};

export const fetchCampaignIdByType = async (
  campaignType: CampaignType,
  userId?: string
): Promise<string | null> => {
  try {
    const query = supabase
      .from('campaigns')
      .select('campaign_id')
      .eq('type', campaignType)
      .eq('status', 'active')
      .limit(1);

    if (userId) {
      query.eq('user_id', userId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error(`[campaignHelpers] Error fetching campaign for type ${campaignType}:`, error);
      return null;
    }

    if (!data) {
      console.warn(`[campaignHelpers] No active campaign found for type: ${campaignType}`);
      return null;
    }

    return data.campaign_id;
  } catch (error) {
    console.error('[campaignHelpers] Exception in fetchCampaignIdByType:', error);
    return null;
  }
};

export const fetchInboundCampaignId = async (
  inboundType: string | null | undefined,
  userId?: string
): Promise<string | null> => {
  try {
    const isNonUrgent = inboundType === 'non_urgent';
    const query = supabase
      .from('campaigns')
      .select('campaign_id, name')
      .eq('type', 'inbound')
      .eq('status', 'active')
      .limit(10);

    if (userId) {
      query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error || !data || data.length === 0) {
      return null;
    }

    if (isNonUrgent) {
      const nonUrgent = data.find((c: any) => c.name?.includes('Non-Urgent'));
      if (nonUrgent) return nonUrgent.campaign_id;
    }

    const urgent = data.find((c: any) => !c.name?.includes('Non-Urgent'));
    return urgent?.campaign_id || data[0].campaign_id;
  } catch (error) {
    console.error('[campaignHelpers] Exception in fetchInboundCampaignId:', error);
    return null;
  }
};

const INBOUND_INQUIRY_TEMPLATE_KEYS = [
  'email_inbound_first',
  'email_inbound_fu_1',
  'email_inbound_fu_2',
];

const INBOUND_LEAD_MAGNET_TEMPLATE_KEYS = [
  'email_inbound_lm_first',
  'email_inbound_lm_fu_1',
  'email_inbound_lm_fu_2',
];

export interface InboundTemplateReadiness {
  urgentReady: boolean;
  nonUrgentReady: boolean;
}

export const checkInboundTemplateReadiness = async (
  clientId: string
): Promise<InboundTemplateReadiness> => {
  try {
    const { data, error } = await supabase
      .from('email_templates')
      .select('template_key')
      .eq('client_id', clientId)
      .eq('status', 'approved')
      .in('template_key', [...INBOUND_INQUIRY_TEMPLATE_KEYS, ...INBOUND_LEAD_MAGNET_TEMPLATE_KEYS]);

    if (error || !data) {
      return { urgentReady: false, nonUrgentReady: false };
    }

    const approvedKeys = new Set(data.map((r: any) => r.template_key));
    return {
      urgentReady: INBOUND_INQUIRY_TEMPLATE_KEYS.every(k => approvedKeys.has(k)),
      nonUrgentReady: INBOUND_LEAD_MAGNET_TEMPLATE_KEYS.every(k => approvedKeys.has(k)),
    };
  } catch (error) {
    console.error('[campaignHelpers] Exception in checkInboundTemplateReadiness:', error);
    return { urgentReady: false, nonUrgentReady: false };
  }
};

export const getSentToFlags = (action: string) => {
  switch (action) {
    case 'Call':
      return { sent_to_ca: true };
    case 'WhatsApp':
      return { sent_to_wa: true };
    case 'Email':
      return { sent_to_ea: true };
    case 'LinkedIn':
      return { sent_to_la: true };
    case 'Omni-Channel':
    case 'Inbound':
      return {
        sent_to_ca: true,
        sent_to_wa: true,
        sent_to_ea: true,
        sent_to_la: true
      };
    default:
      return {};
  }
};

export interface CampaignValidationResult {
  isValid: boolean;
  error?: string;
  conflictingCampaigns?: Array<{ type: string; companyName: string; leadId: string }>;
  conflictingLeadIds?: string[];
}

export const validateCampaignAssignment = async (
  leadIds: string[],
  newCampaignType: CampaignType
): Promise<CampaignValidationResult> => {
  try {
    // Fetch existing campaign assignments for these leads
    const { data: existingAssignments, error } = await supabase
      .from('leads_campaigns')
      .select('lead_id, campaign_type')
      .in('lead_id', leadIds);

    if (error) {
      console.error('[campaignHelpers] Error fetching existing assignments:', error);
      return { isValid: false, error: 'Failed to check existing assignments' };
    }

    if (!existingAssignments || existingAssignments.length === 0) {
      return { isValid: true };
    }

    // Build a map of lead_id -> campaign types
    const leadCampaignMap = new Map<string, string[]>();
    existingAssignments.forEach(assignment => {
      const types = leadCampaignMap.get(assignment.lead_id) || [];
      types.push(assignment.campaign_type);
      leadCampaignMap.set(assignment.lead_id, types);
    });

    // Check for conflicts
    const conflicts: Array<{ type: string; companyName: string; leadId: string }> = [];
    const conflictingLeadIds: string[] = [];

    for (const [leadId, existingTypes] of leadCampaignMap.entries()) {
      let hasConflict = false;
      let conflictType = '';

      // Rule 1: Check for duplicates
      if (existingTypes.includes(newCampaignType)) {
        hasConflict = true;
        conflictType = newCampaignType;
      }

      // Rule 2: Check Omni/Inbound conflicts
      if (newCampaignType === 'omni' || newCampaignType === 'inbound') {
        if (existingTypes.length > 0) {
          hasConflict = true;
          const existingCampaigns = existingTypes.join(', ');
          conflictType = `${newCampaignType} (conflicts with existing ${existingCampaigns})`;
        }
      } else {
        if (existingTypes.includes('omni')) {
          hasConflict = true;
          conflictType = `${newCampaignType} (conflicts with existing Omni)`;
        }
        if (existingTypes.includes('inbound')) {
          hasConflict = true;
          conflictType = `${newCampaignType} (conflicts with existing Inbound)`;
        }
      }

      if (hasConflict) {
        // Fetch lead details for error message
        const { data: lead } = await supabase
          .from('leads_tracking')
          .select('company_name')
          .eq('lead_id', leadId)
          .maybeSingle();

        conflicts.push({
          type: conflictType,
          companyName: lead?.company_name || 'Unknown Company',
          leadId: leadId
        });
        conflictingLeadIds.push(leadId);
      }
    }

    if (conflicts.length > 0) {
      const errorMsg = conflicts.length === 1
        ? `Cannot assign: ${conflicts[0].companyName} is already in ${conflicts[0].type} campaign`
        : `Cannot assign ${conflicts.length} leads: Already assigned or conflicts detected`;

      return {
        isValid: false,
        error: errorMsg,
        conflictingCampaigns: conflicts,
        conflictingLeadIds: conflictingLeadIds
      };
    }

    return { isValid: true };
  } catch (error) {
    console.error('[campaignHelpers] Exception in validateCampaignAssignment:', error);
    return { isValid: false, error: 'Validation error occurred' };
  }
};

const CHANNEL_REQUIRED_FIELDS: Record<string, { field: string; label: string }> = {
  call: { field: 'dm_mobile_1', label: 'Phone number' },
  whatsapp: { field: 'dm_mobile_1', label: 'Phone number' },
  email: { field: 'dm_email_1', label: 'Email address' },
  linkedin: { field: 'dm_linkedin', label: 'LinkedIn profile' },
};

export interface ContactValidationResult {
  validLeadIds: string[];
  missingContactLeads: Array<{ leadId: string; companyName: string; missingField: string }>;
}

export const validateContactForChannel = (
  leads: Array<{ lead_id: string; company_name?: string; dm_mobile_1?: string; dm_email_1?: string; dm_linkedin?: string }>,
  campaignType: CampaignType
): ContactValidationResult => {
  const validLeadIds: string[] = [];
  const missingContactLeads: ContactValidationResult['missingContactLeads'] = [];

  if (campaignType === 'omni' || campaignType === 'inbound') {
    for (const lead of leads) {
      const hasPhone = !!lead.dm_mobile_1?.trim();
      const hasEmail = !!lead.dm_email_1?.trim();
      const hasLinkedIn = !!lead.dm_linkedin?.trim();

      if (!hasPhone && !hasEmail && !hasLinkedIn) {
        missingContactLeads.push({
          leadId: lead.lead_id,
          companyName: lead.company_name || 'Unknown Company',
          missingField: 'No contact details (phone, email, or LinkedIn)',
        });
      } else {
        validLeadIds.push(lead.lead_id);
      }
    }
    return { validLeadIds, missingContactLeads };
  }

  const requirement = CHANNEL_REQUIRED_FIELDS[campaignType];
  if (!requirement) {
    return { validLeadIds: leads.map(l => l.lead_id), missingContactLeads: [] };
  }

  for (const lead of leads) {
    const value = (lead as any)[requirement.field];
    if (!value || !value.trim()) {
      missingContactLeads.push({
        leadId: lead.lead_id,
        companyName: lead.company_name || 'Unknown Company',
        missingField: requirement.label,
      });
    } else {
      validLeadIds.push(lead.lead_id);
    }
  }

  return { validLeadIds, missingContactLeads };
};
